import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  RepairRequest,
  ServiceProvider,
  ServiceVerificationStatus,
  UserRole,
} from '../types/firestore';
import { repairRequestService } from '../services/repairRequestService';
import { serviceProviderService } from '../services/serviceProviderService';
import RepairRequestCard from '../components/Request/RepairRequestCard';
import { Alert, Button, Card } from '../components/common/UIComponents';
import { PageErrorState, PageLoadingState } from '../components/common/ViewStates';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  getRepairRequestStatusLabel,
  getServiceDashboardBucket,
  isStaleRepairRequest,
} from '../utils/repairRequestWorkflow';
import { buildOrderCardState } from '../utils/orderCardRuntime';
import {
  getRepairRequestId,
  getRepairRequestUnreadState,
  isRepairRequestAssignedToServiceUser,
} from '../utils/repairRequestReadModel';
import { ServiceWorkspaceShell } from '../components/Service/ServiceWorkspaceShell';
import ServiceCommandCenterLayout from '../components/Service/ServiceCommandCenterLayout';
import {
  getServiceVerificationReviewReason,
  isServiceProfileComplete,
  resolveServiceVerificationStatus,
} from '../utils/serviceVerification';
import {
  getRequestQueueFilterActiveRailClasses,
  getRequestQueueFilterCountClasses,
  getRequestQueueFilterTabClasses,
  type RequestQueueBucket,
  type RequestQueueTone,
} from '../utils/requestQueueVisuals';
import { getRepairRequestDevicePresentation } from '../utils/repairRequestPresentation';
import {
  addDaysToServicePlannerDayKey,
  dateFromServicePlannerDayKey,
  deriveServicePlannerItems,
  getServicePlannerDayKey,
  type ServicePlannerDayWorkloadSummary,
  type ServicePlannerItem,
  type ServicePlannerWeekDay,
} from '../utils/servicePlannerReadModel';

type ActiveOrderFilter = 'all' | 'attention' | 'in_progress' | 'history';
type OrdersWorkbenchBucket = Exclude<ActiveOrderFilter, 'all'>;
type OrdersFilterTabTone = RequestQueueTone;

type DerivedOrderRecord = {
  request: RepairRequest;
  bucket: OrdersWorkbenchBucket;
  lastRelevantActivityAtMs: number;
  requiresAttentionSignal: boolean;
};

type DerivedServiceOrders = {
  all: RepairRequest[];
  attention: RepairRequest[];
  inProgress: RepairRequest[];
  history: RepairRequest[];
  staleActive: RepairRequest[];
  bucketByRequestId: Record<string, OrdersWorkbenchBucket>;
  counts: {
    attention: number;
    in_progress: number;
    history: number;
  };
};

type OrdersFilterTabConfig = {
  key: ActiveOrderFilter;
  label: string;
  count: number;
  tone: OrdersFilterTabTone;
  railKey: RequestQueueBucket;
};

type ServiceQueueAttentionKind = 'needs_action' | 'fresh_request' | 'unread_messages';
type ServiceQueueAttentionState = {
  kind: ServiceQueueAttentionKind;
  level: 'static' | 'primary';
};

const SERVICE_BUCKET_META: Record<
  OrdersWorkbenchBucket,
  {
    label: string;
    tone: OrdersFilterTabTone;
    emptyLabel: string;
    railSemantics: string;
  }
> = {
  attention: {
    label: 'Do reakcji',
    tone: 'danger',
    emptyLabel: 'Brak spraw wymagających reakcji.',
    railSemantics: 'urgent-requires-reaction',
  },
  in_progress: {
    label: 'W toku',
    tone: 'warning',
    emptyLabel: 'Brak spraw w toku.',
    railSemantics: 'in-progress-attention',
  },
  history: {
    label: 'Historia',
    tone: 'success',
    emptyLabel: 'Brak spraw historycznych.',
    railSemantics: 'completed-positive-closure',
  },
};

const ServiceDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [providerProfile, setProviderProfile] = useState<ServiceProvider | null>(null);
  const [loadingProviderProfile, setLoadingProviderProfile] = useState<boolean>(true);
  const [providerProfileError, setProviderProfileError] = useState<string | null>(null);
  const [requests, setRequests] = useState<RepairRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState<boolean>(true);
  const [requestsError, setRequestsError] = useState<string | null>(null);
  const [activeOrderFilter, setActiveOrderFilter] = useState<ActiveOrderFilter>(() =>
    getServiceOrderFilterFromSearch(location.search)
  );
  const [planningContextRequestId, setPlanningContextRequestId] = useState<string | null>(() =>
    getServiceExpandedRequestFromSearch(location.search)
  );
  const [plannerDayKey, setPlannerDayKey] = useState(() => getServicePlannerDayKey(new Date()));
  const [staleOnly, setStaleOnly] = useState(false);
  const hasReceivedRequestsRef = useRef(false);
  const lastSyncedSearchRef = useRef<string | null>(null);
  const [subscriptionRetryNonce, setSubscriptionRetryNonce] = useState<number>(0);
  const isDesktopWorkbench = useMediaQuery('(min-width: 1024px)');

  const loadProviderProfile = useCallback(async () => {
    if (!currentUser) {
      setProviderProfile(null);
      setLoadingProviderProfile(false);
      setProviderProfileError('Musisz być zalogowany jako serwis, aby otworzyć moduł serwisowy.');
      return;
    }

    const cachedProfile = serviceProviderService.getCachedServiceProviderProfile(currentUser.uid);
    const hasCachedProfile = cachedProfile !== undefined;
    if (hasCachedProfile) {
      setProviderProfile(cachedProfile);
      setLoadingProviderProfile(false);
    } else {
      setLoadingProviderProfile(true);
    }
    setProviderProfileError(null);
    try {
      const profile = await serviceProviderService.getServiceProviderProfile(currentUser.uid, {
        forceRefresh: true,
      });
      setProviderProfile(profile);
    } catch (error) {
      console.error('Błąd podczas pobierania statusu profilu serwisu:', error);
      if (!hasCachedProfile) {
        setProviderProfileError(
          'Nie udało się pobrać statusu profilu serwisu. Odśwież widok i spróbuj ponownie.'
        );
      }
    } finally {
      setLoadingProviderProfile(false);
    }
  }, [currentUser]);

  useEffect(() => {
    void loadProviderProfile();
  }, [loadProviderProfile]);

  const onboardingBootstrapStatus = useMemo(() => {
    const candidate = String((location.state as any)?.onboardingBootstrapStatus || '')
      .trim()
      .toLowerCase();
    if (
      candidate === 'profile_incomplete' ||
      candidate === 'pending_approval' ||
      candidate === 'changes_requested' ||
      candidate === 'approved'
    ) {
      return candidate as ServiceVerificationStatus;
    }
    return null;
  }, [location.state]);

  const serviceVerificationStatus = useMemo(
    () =>
      providerProfile
        ? resolveServiceVerificationStatus(providerProfile)
        : onboardingBootstrapStatus || 'profile_incomplete',
    [onboardingBootstrapStatus, providerProfile]
  );
  const serviceVerificationReason = useMemo(
    () => getServiceVerificationReviewReason(providerProfile),
    [providerProfile]
  );
  const isApprovedServiceProfile = serviceVerificationStatus === 'approved';

  useEffect(() => {
    if (loadingProviderProfile) {
      return;
    }

    if (!isApprovedServiceProfile) {
      setRequests([]);
      setLoadingRequests(false);
      setRequestsError(null);
      hasReceivedRequestsRef.current = false;
      return;
    }

    if (!currentUser?.uid) {
      setRequests([]);
      setLoadingRequests(false);
      setRequestsError('Musisz być zalogowany jako serwis, aby zobaczyć panel operacyjny.');
      hasReceivedRequestsRef.current = false;
      return;
    }

    hasReceivedRequestsRef.current = false;
    setLoadingRequests(true);
    setRequestsError(null);

    const unsubscribe = repairRequestService.subscribeToRequestsForServiceProvider(
      currentUser.uid,
      'all',
      'updatedAt',
      (nextRequests) => {
        const assignedRequests = nextRequests.filter((request) =>
          isRepairRequestAssignedToServiceUser(request, currentUser.uid)
        );
        hasReceivedRequestsRef.current = true;
        setRequests(assignedRequests);
        setRequestsError(null);
        setLoadingRequests(false);
      },
      (error) => {
        console.error('Blad subskrypcji zlecen serwisanta:', error);
        if (!hasReceivedRequestsRef.current) {
          setRequestsError(
            'Nie udało się pobrać przypisanych zleceń serwisu. Sprawdź połączenie i spróbuj ponownie.'
          );
          setLoadingRequests(false);
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [currentUser, isApprovedServiceProfile, loadingProviderProfile, subscriptionRetryNonce]);

  const retryRequestsSubscription = useCallback(() => {
    setSubscriptionRetryNonce((prev) => prev + 1);
  }, []);

  const ordersWorkbench = useMemo(() => deriveServiceOrdersWorkbench(requests), [requests]);
  const requestById = useMemo(() => {
    const nextMap = new Map<string, RepairRequest>();
    ordersWorkbench.all.forEach((request) => {
      const requestId = getRepairRequestId(request);
      if (requestId) {
        nextMap.set(requestId, request);
      }
    });
    return nextMap;
  }, [ordersWorkbench.all]);
  const derivedOrders = useMemo(() => {
    if (activeOrderFilter === 'attention') {
      return ordersWorkbench.attention;
    }
    if (activeOrderFilter === 'in_progress') {
      return ordersWorkbench.inProgress;
    }
    if (activeOrderFilter === 'history') {
      return ordersWorkbench.history;
    }
    return ordersWorkbench.all;
  }, [activeOrderFilter, ordersWorkbench]);
  const staleActiveRequestIds = useMemo(
    () =>
      new Set(
        ordersWorkbench.staleActive
          .map((request) => getRepairRequestId(request))
          .filter((value): value is string => Boolean(value))
      ),
    [ordersWorkbench.staleActive]
  );
  const staleOrdersInCurrentView = useMemo(
    () =>
      derivedOrders.filter((request) => {
        const requestId = getRepairRequestId(request);
        return requestId ? staleActiveRequestIds.has(requestId) : false;
      }),
    [derivedOrders, staleActiveRequestIds]
  );
  const visibleOrders = useMemo(
    () => (staleOnly ? staleOrdersInCurrentView : derivedOrders),
    [derivedOrders, staleOnly, staleOrdersInCurrentView]
  );
  const showStaleAlert = activeOrderFilter !== 'history' && staleOrdersInCurrentView.length > 0;
  const staleAlertCopy = useMemo(
    () =>
      staleOrdersInCurrentView.length === 1
        ? '1 sprawa bez aktualizacji 48h'
        : `${staleOrdersInCurrentView.length} spraw bez aktualizacji 48h`,
    [staleOrdersInCurrentView.length]
  );
  const activeRequestFromSearch = useMemo(
    () => getServiceExpandedRequestFromSearch(location.search),
    [location.search]
  );
  useEffect(() => {
    setPlanningContextRequestId(activeRequestFromSearch);
  }, [activeRequestFromSearch]);

  const attentionSourceOrders = useMemo(() => visibleOrders, [visibleOrders]);
  const visibleOrderAttentionStates = useMemo(() => {
    const states: Record<string, ServiceQueueAttentionState> = {};
    let hasPrimaryCue = false;

    attentionSourceOrders.forEach((request) => {
      const requestId = getRepairRequestId(request);
      const kind = resolveServiceQueueAttentionKind(request);
      if (!requestId || !kind) {
        return;
      }

      states[requestId] = {
        kind,
        level: hasPrimaryCue ? 'static' : 'primary',
      };
      hasPrimaryCue = true;
    });

    return states;
  }, [attentionSourceOrders]);

  const selectedPlanningContextRequest = useMemo(
    () => (planningContextRequestId ? requestById.get(planningContextRequestId) || null : null),
    [planningContextRequestId, requestById]
  );
  const selectedPlanningContextRequestId = selectedPlanningContextRequest
    ? getRepairRequestId(selectedPlanningContextRequest)
    : 'bez godziny';
  const plannerReadModel = useMemo(
    () => deriveServicePlannerItems(ordersWorkbench.all, { selectedDayKey: plannerDayKey }),
    [ordersWorkbench.all, plannerDayKey]
  );
  useEffect(() => {
    if (!selectedPlanningContextRequestId || !selectedPlanningContextRequest) {
      return;
    }
    const scheduledStartAt =
      selectedPlanningContextRequest.serviceSchedule?.status === 'scheduled'
        ? toDateValue(selectedPlanningContextRequest.serviceSchedule.scheduledStartAt)
        : null;
    if (!scheduledStartAt) {
      return;
    }
    const nextDayKey = getServicePlannerDayKey(scheduledStartAt);
    setPlannerDayKey((currentDayKey) =>
      currentDayKey === nextDayKey ? currentDayKey : nextDayKey
    );
  }, [selectedPlanningContextRequest, selectedPlanningContextRequestId]);
  const counterConfig: OrdersFilterTabConfig[] = [
    {
      key: 'all',
      label: 'Kolejka',
      count: ordersWorkbench.all.length,
      tone: 'neutral',
      railKey: 'neutral',
    },
    {
      key: 'attention',
      label: SERVICE_BUCKET_META.attention.label,
      count: ordersWorkbench.counts.attention,
      tone: SERVICE_BUCKET_META.attention.tone,
      railKey: 'attention',
    },
    {
      key: 'in_progress',
      label: SERVICE_BUCKET_META.in_progress.label,
      count: ordersWorkbench.counts.in_progress,
      tone: SERVICE_BUCKET_META.in_progress.tone,
      railKey: 'in_progress',
    },
    {
      key: 'history',
      label: SERVICE_BUCKET_META.history.label,
      count: ordersWorkbench.counts.history,
      tone: SERVICE_BUCKET_META.history.tone,
      railKey: 'history',
    },
  ];

  useEffect(() => {
    const nextParams = new URLSearchParams(location.search);
    const serializedFilter = serializeServiceOrderFilter(activeOrderFilter);
    if (serializedFilter) {
      nextParams.set('filter', serializedFilter);
    } else {
      nextParams.delete('filter');
    }

    if (activeRequestFromSearch) {
      nextParams.set('request', activeRequestFromSearch);
    } else {
      nextParams.delete('request');
    }

    // Cleanup legacy params after migration to single filter + single expanded request model.
    nextParams.delete('history');
    nextParams.delete('activeRequest');
    nextParams.delete('historyRequest');

    const nextSearch = nextParams.toString();
    const currentSearch = new URLSearchParams(location.search).toString();
    if (nextSearch === currentSearch) {
      lastSyncedSearchRef.current = nextSearch;
      return;
    }
    if (lastSyncedSearchRef.current === nextSearch) {
      return;
    }
    lastSyncedSearchRef.current = nextSearch;

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [activeOrderFilter, activeRequestFromSearch, location.pathname, location.search, navigate]);

  const buildDetailHref = useCallback(
    (requestId: string, section?: 'messages' | 'description' | 'settlement') => {
      const returnParams = new URLSearchParams();
      const serializedFilter = serializeServiceOrderFilter(activeOrderFilter);
      if (serializedFilter) {
        returnParams.set('filter', serializedFilter);
      }
      returnParams.set('request', requestId);

      const returnTo = `/app/service${returnParams.toString() ? `?${returnParams.toString()}` : ''}`;
      const detailParams = new URLSearchParams();
      detailParams.set('returnTo', returnTo);
      detailParams.set('workspace', 'service');
      if (section) {
        detailParams.set('section', section);
      }
      return `/app/request/${requestId}?${detailParams.toString()}`;
    },
    [activeOrderFilter]
  );

  const buildPlannerHref = useCallback((requestId: string) => {
    const params = new URLSearchParams();
    params.set('planRequestId', requestId);
    return `/app/service/planner?${params.toString()}`;
  }, []);

  const selectRequestForPlanningContext = useCallback(
    (requestId: string) => {
      setPlanningContextRequestId(requestId);

      const nextParams = new URLSearchParams(location.search);
      const serializedFilter = serializeServiceOrderFilter(activeOrderFilter);
      if (serializedFilter) {
        nextParams.set('filter', serializedFilter);
      } else {
        nextParams.delete('filter');
      }
      nextParams.set('request', requestId);
      nextParams.delete('history');
      nextParams.delete('activeRequest');
      nextParams.delete('historyRequest');

      const nextSearch = nextParams.toString();
      const currentSearch = new URLSearchParams(location.search).toString();
      if (nextSearch === currentSearch) {
        return;
      }

      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true }
      );
    },
    [activeOrderFilter, location.pathname, location.search, navigate]
  );

  if (loadingProviderProfile) {
    return (
      <div className="py-3 md:py-12">
        <div className="mx-auto w-full max-w-4xl space-y-4 px-3 sm:px-4 lg:px-6">
          <PageLoadingState
            title="Sprawdzanie statusu profilu serwisu"
            message="Weryfikujemy, czy profil serwisu jest gotowy do pracy operacyjnej."
          />
        </div>
      </div>
    );
  }

  if (providerProfileError) {
    return (
      <div className="mx-auto max-w-xl px-3 py-8 md:py-12">
        <PageErrorState
          title="Nie udało się otworzyć modułu serwisu"
          message={providerProfileError}
          actionLabel="Spróbuj ponownie"
          onAction={loadProviderProfile}
        />
      </div>
    );
  }

  if (!isApprovedServiceProfile) {
    return (
      <ServiceOnboardingState
        status={serviceVerificationStatus}
        hasProfile={Boolean(providerProfile)}
        isProfileComplete={isServiceProfileComplete(providerProfile)}
        reviewReason={serviceVerificationReason}
        onRefresh={loadProviderProfile}
        onOpenProfileForm={() => {
          const query = new URLSearchParams();
          query.set('onboarding', '1');
          if (serviceVerificationStatus === 'changes_requested') {
            query.set('resubmit', '1');
          }
          navigate(`/app/service-profile?${query.toString()}`);
        }}
        onOpenClientRequests={() => navigate('/app/my-requests')}
      />
    );
  }

  if (loadingRequests) {
    return (
      <div className="py-3 md:py-12">
        <div className="mx-auto w-full max-w-6xl space-y-4 px-3 sm:px-4 lg:px-6">
          <PageLoadingState
            title="Ładowanie panelu serwisu"
            message="Pobieramy przypisane zlecenia, liczniki i historię."
          />
        </div>
      </div>
    );
  }

  if (requestsError) {
    return (
      <div className="mx-auto max-w-xl px-3 py-8 md:py-12">
        <PageErrorState
          title="Nie udało się załadować panelu serwisu"
          message={requestsError}
          actionLabel="Spróbuj ponownie"
          onAction={retryRequestsSubscription}
        />
      </div>
    );
  }

  const renderRequestCard = (
    request: RepairRequest,
    queueBucket?: OrdersWorkbenchBucket,
    onPlannerContextSelect?: () => void
  ) => {
    const cardId = getRepairRequestId(request);
    if (!cardId) {
      return null;
    }
    const fullViewHref = buildDetailHref(cardId);
    const chatHref = buildDetailHref(cardId, 'messages');
    const plannerHref = buildPlannerHref(cardId);
    const isRequestStale = staleActiveRequestIds.has(cardId);
    const resolvedQueueBucket = queueBucket || ordersWorkbench.bucketByRequestId[cardId];
    const attentionState = visibleOrderAttentionStates[cardId];

    return (
      <RepairRequestCard
        key={cardId}
        request={request}
        currentUserRole={UserRole.SERVICE_ADMIN}
        showOperationalSignals
        isStale={isRequestStale}
        serviceDashboardMode
        serviceWorkshopMode
        compactListMode
        disableInlineExpand
        currentUserId={currentUser?.uid}
        workspaceSimpleMode
        fullViewHref={fullViewHref}
        chatHref={chatHref}
        plannerHref={plannerHref}
        onPlannerContextSelect={onPlannerContextSelect}
        queueBucket={resolvedQueueBucket}
        serviceQueueAttentionLevel={attentionState?.level || 'none'}
        serviceQueueAttentionKind={attentionState?.kind}
      />
    );
  };

  const renderDesktopRequestCard = (
    request: RepairRequest,
    queueBucket?: OrdersWorkbenchBucket
  ) => {
    const cardId = getRepairRequestId(request);
    if (!cardId) {
      return null;
    }
    const isSelected = selectedPlanningContextRequestId === cardId;

    return (
      <div
        key={cardId}
        data-testid={`service-workbench-card-frame-${cardId}`}
        data-selected-request={isSelected ? 'true' : 'false'}
        data-selected-planning-context={isSelected ? 'true' : 'false'}
        className={`rs-workbench-card-frame ${
          isSelected ? 'rs-workbench-card-frame--selected' : ''
        }`}
        onClick={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest('a,button,input,textarea,select,[role="button"]')) {
            return;
          }
          selectRequestForPlanningContext(cardId);
        }}
      >
        {renderRequestCard(request, queueBucket, () => selectRequestForPlanningContext(cardId))}
      </div>
    );
  };

  const selectedDetailHref =
    selectedPlanningContextRequestId && selectedPlanningContextRequest
      ? buildDetailHref(selectedPlanningContextRequestId)
      : null;
  const selectedPlannerHref =
    selectedPlanningContextRequestId && selectedPlanningContextRequest
      ? buildPlannerHref(selectedPlanningContextRequestId)
      : null;
  const activeFilterConfig =
    counterConfig.find((tab) => tab.key === activeOrderFilter) || counterConfig[0];
  const activeQueueLabel =
    activeOrderFilter === 'all' ? 'Kolejka operacyjna' : activeFilterConfig.label;
  const visibleQueueCountLabel =
    visibleOrders.length === 1 ? '1 sprawa w widoku' : `${visibleOrders.length} spraw w widoku`;
  const handleOrderFilterToggle = (nextFilter: ActiveOrderFilter) => {
    setActiveOrderFilter((prev) => {
      if (nextFilter === 'all') {
        return 'all';
      }
      return prev === nextFilter ? 'all' : nextFilter;
    });
  };

  const mobileQueue = (
    <div className="rs-command-center-mobile">
      <h1 className="sr-only">Zlecenia serwisu</h1>

      <OrderStatusBucketFilter
        tabs={counterConfig}
        activeOrderFilter={activeOrderFilter}
        onToggle={handleOrderFilterToggle}
      />

      <section
        id="service-dashboard-queue-content"
        data-testid="service-dashboard-queue-content"
        className="relative space-y-2 sm:space-y-2.5"
      >
        <h2 className="sr-only">Lista zleceń serwisu</h2>
        {showStaleAlert && (
          <StaleSummaryToggle
            active={staleOnly}
            label={staleOnly ? 'Pokazujesz sprawy 48h' : staleAlertCopy}
            onToggle={() => setStaleOnly((current) => !current)}
          />
        )}

        {requests.length === 0 ? (
          <ServiceQueueEmptyState />
        ) : visibleOrders.length === 0 ? (
          <ServiceQueueFilterEmptyState
            staleOnly={staleOnly}
            onShowAll={() => {
              if (staleOnly) {
                setStaleOnly(false);
                return;
              }
              setActiveOrderFilter('all');
            }}
          />
        ) : (
          <div className="space-y-2 [touch-action:pan-y] sm:space-y-2.5">
            {visibleOrders.map((request) => renderRequestCard(request))}
          </div>
        )}
      </section>
    </div>
  );

  const desktopWorkbench = (
    <div className="rs-command-center-desktop">
      <h1 className="sr-only">Zlecenia serwisu</h1>

      {requests.length === 0 ? (
        <ServiceQueueEmptyState />
      ) : (
        <div className="rs-command-center-grid">
          <section
            className="rs-command-queue-panel"
            aria-label="Operacyjna kolejka serwisu"
            data-testid="service-command-queue-panel"
          >
            <div className="rs-command-queue-header">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-light-400">
                  Kolejka serwisu
                </p>
                <h2 className="mt-0.5 truncate text-lg font-semibold leading-tight text-primary-light-100">
                  {activeQueueLabel}
                </h2>
                <p className="mt-1 text-xs text-primary-light-400">{visibleQueueCountLabel}</p>
              </div>
            </div>

            <OrderStatusBucketFilter
              tabs={counterConfig}
              activeOrderFilter={activeOrderFilter}
              onToggle={handleOrderFilterToggle}
            />

            {showStaleAlert && (
              <div className="mt-2">
                <StaleSummaryToggle
                  active={staleOnly}
                  label={staleOnly ? 'Pokazujesz sprawy 48h' : staleAlertCopy}
                  onToggle={() => setStaleOnly((current) => !current)}
                />
              </div>
            )}

            <section
              id="service-dashboard-queue-content"
              data-testid="service-dashboard-queue-content"
              className="mt-3 min-w-0 space-y-2.5"
            >
              <h2 className="sr-only">Lista zleceń serwisu</h2>
              {visibleOrders.length === 0 ? (
                <ServiceQueueFilterEmptyState
                  staleOnly={staleOnly}
                  onShowAll={() => {
                    if (staleOnly) {
                      setStaleOnly(false);
                      return;
                    }
                    setActiveOrderFilter('all');
                  }}
                />
              ) : (
                <div className="space-y-2.5">
                  {visibleOrders.map((request) => {
                    const requestId = getRepairRequestId(request);
                    const queueBucket = requestId
                      ? ordersWorkbench.bucketByRequestId[requestId]
                      : undefined;
                    return renderDesktopRequestCard(request, queueBucket);
                  })}
                </div>
              )}
            </section>
          </section>

          <ServicePlannerCompanionPanel
            planner={plannerReadModel}
            selectedDayKey={plannerDayKey}
            selectedPlanningRequest={selectedPlanningContextRequest}
            selectedPlanningRequestId={selectedPlanningContextRequestId}
            detailHref={selectedDetailHref}
            plannerHref={selectedPlannerHref}
            onSelectDay={setPlannerDayKey}
            onPreviousWeek={() =>
              setPlannerDayKey((currentDayKey) => addDaysToServicePlannerDayKey(currentDayKey, -7))
            }
            onNextWeek={() =>
              setPlannerDayKey((currentDayKey) => addDaysToServicePlannerDayKey(currentDayKey, 7))
            }
            buildDetailHref={buildDetailHref}
          />
        </div>
      )}
    </div>
  );

  return (
    <ServiceWorkspaceShell
      surfaceVariant="orders_focus"
      contentClassName="space-y-0 p-0 sm:space-y-0 sm:p-0"
    >
      <ServiceCommandCenterLayout
        mode={isDesktopWorkbench ? 'desktop' : 'mobile'}
        mobile={mobileQueue}
        desktop={desktopWorkbench}
      />
    </ServiceWorkspaceShell>
  );
};

const StaleSummaryToggle: React.FC<{
  active: boolean;
  label: string;
  onToggle: () => void;
}> = ({ active, label, onToggle }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onToggle}
    className={`rs-stale-summary ${active ? 'rs-stale-summary--active' : ''}`}
  >
    <ExclamationTriangleIcon
      className="h-4 w-4 flex-shrink-0 text-status-warning-500/95"
      aria-hidden="true"
    />
    <span className="min-w-0 flex-1 truncate text-xs font-medium text-primary-light-100 sm:text-sm">
      {label}
    </span>
  </button>
);

const ServiceQueueEmptyState: React.FC = () => (
  <div className="rounded-lg bg-primary-dark-200/14 px-4 py-4 ring-1 ring-primary-dark-300/18 sm:px-5 sm:py-5">
    <h2 className="text-base font-semibold text-primary-light-100">Brak zleceń serwisowych</h2>
    <p className="mt-1.5 max-w-prose text-sm leading-relaxed text-primary-light-300">
      Gdy pojawią się nowe sprawy przypisane do Twojego serwisu, zobaczysz je tutaj w kolejce
      operacyjnej.
    </p>
  </div>
);

const ServiceQueueFilterEmptyState: React.FC<{
  staleOnly: boolean;
  onShowAll: () => void;
}> = ({ staleOnly, onShowAll }) => (
  <div className="rounded-lg bg-primary-dark-200/14 px-4 py-4 ring-1 ring-primary-dark-300/18 sm:px-5 sm:py-5">
    <h2 className="text-base font-semibold text-primary-light-100">
      {staleOnly ? 'Brak spraw 48h' : 'Brak spraw dla aktywnego filtra'}
    </h2>
    <p className="mt-1.5 text-sm leading-relaxed text-primary-light-300">
      {staleOnly
        ? 'Wróć do pełnej listy.'
        : 'Wróć do pełnej listy, aby zobaczyć wszystkie przypisane sprawy.'}
    </p>
    <Button variant="neutral" size="sm" className="mt-3 min-h-10 px-4" onClick={onShowAll}>
      Pokaż wszystkie
    </Button>
  </div>
);

const ServicePlannerCompanionPanel: React.FC<{
  planner: ReturnType<typeof deriveServicePlannerItems>;
  selectedDayKey: string;
  selectedPlanningRequest: RepairRequest | null;
  selectedPlanningRequestId: string | null;
  detailHref: string | null;
  plannerHref: string | null;
  onSelectDay: (dayKey: string) => void;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  buildDetailHref: (requestId: string) => string;
}> = ({
  planner,
  selectedDayKey,
  selectedPlanningRequest,
  selectedPlanningRequestId,
  detailHref,
  plannerHref,
  onSelectDay,
  onPreviousWeek,
  onNextWeek,
  buildDetailHref,
}) => {
  const weekScheduledCount = planner.weekItems.reduce((sum, day) => sum + day.scheduledCount, 0);
  const selectedPlanningItem = selectedPlanningRequestId
    ? [...planner.scheduledItems, ...planner.unscheduledItems].find(
        (item) => item.requestId === selectedPlanningRequestId
      ) || null
    : null;
  const selectedDevice = selectedPlanningRequest
    ? getRepairRequestDevicePresentation(selectedPlanningRequest)
    : null;
  const selectedPlanTimeLabel = selectedPlanningItem?.scheduledStartAt
    ? formatServiceTimestamp(selectedPlanningItem.scheduledStartAt)
    : null;
  const selectedPlanLabel = selectedPlanTimeLabel
    ? `Termin pracy: ${selectedPlanTimeLabel}`
    : 'Brak terminu pracy serwisu';
  const plannerFullHref = plannerHref || '/app/service/planner';
  const weekNumberLabel = formatPlannerCompanionWeekNumberLabel(planner.weekItems);

  return (
    <aside
      className="rs-command-planner-companion"
      data-testid="service-planner-companion"
      aria-label="Planer i obciążenie serwisu"
    >
      <div className="rs-command-planner-header">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-light-400">
            <span className="hidden xl:inline">Plan tygodnia · {weekNumberLabel}</span>
            <span className="xl:hidden">{weekNumberLabel}</span>
          </p>
          <h2 className="mt-0.5 truncate text-lg font-semibold leading-tight text-primary-light-100">
            <span className="hidden xl:inline">
              {formatPlannerCompanionWeekRange(planner.weekItems)}
            </span>
            <span className="xl:hidden">
              {formatPlannerCompanionCompactWeekRange(planner.weekItems)}
            </span>
          </h2>
        </div>
        <Link
          to={plannerFullHref}
          className="rs-action-primary rs-action-sm shrink-0 focus-outline"
        >
          Otwórz pełny planer
        </Link>
      </div>

      <div className="rs-command-planner-week-nav" aria-label="Nawigacja tygodnia">
        <button type="button" className="rs-command-planner-nav-button" onClick={onPreviousWeek}>
          Poprzedni
        </button>
        <button type="button" className="rs-command-planner-nav-button" onClick={onNextWeek}>
          Następny
        </button>
      </div>

      <div className="rs-command-planner-summary-grid">
        <div
          className="rs-command-planner-summary-cell"
          data-testid="service-planner-summary-selected-day"
        >
          <p>Dzień</p>
          <strong>{planner.selectedDayWorkloadSummary.scheduledCount}</strong>
        </div>
        <div className="rs-command-planner-summary-cell" data-testid="service-planner-summary-week">
          <p>Tydzień</p>
          <strong>{weekScheduledCount}</strong>
        </div>
        <div
          className="rs-command-planner-summary-cell"
          data-testid="service-planner-summary-unscheduled"
        >
          <p>Do planu</p>
          <strong>{planner.unscheduledItems.length}</strong>
        </div>
      </div>

      <section
        className="rs-command-planner-week-agenda"
        data-testid="service-planner-week-agenda"
        aria-label="Agenda widocznego tygodnia"
      >
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-primary-light-100">
              Agenda tygodnia
            </h3>
            <p
              className="mt-1 text-xs font-medium text-primary-light-400"
              data-testid="service-planner-companion-workload"
            >
              Wybrany dzień: {formatPlannerCompanionDayLabel(selectedDayKey)} ·{' '}
              {formatPlannerCompanionWorkloadSummary(planner.selectedDayWorkloadSummary)}
            </p>
          </div>
          {weekScheduledCount > 0 ? (
            <span className="rounded-md bg-ratuj-300/10 px-2 py-1 text-[11px] font-semibold text-ratuj-100">
              {formatPlannerCompanionWorkloadCount(weekScheduledCount)}
            </span>
          ) : planner.selectedDayWorkloadSummary.loadLevel === 'heavy' ? (
            <span className="rounded-md bg-status-warning-500/10 px-2 py-1 text-[11px] font-semibold text-status-warning-300">
              Dużo pracy
            </span>
          ) : null}
        </div>

        <div className="mt-3 space-y-2" data-testid="service-planner-week-agenda-days">
          {planner.weekItems.map((day) => (
            <PlannerCompanionDayAgendaRow
              key={day.dayKey}
              day={day}
              selected={day.dayKey === selectedDayKey}
              onSelectDay={onSelectDay}
              buildDetailHref={buildDetailHref}
            />
          ))}
        </div>
      </section>

      <section
        className="rs-command-planner-context"
        data-testid="service-planning-context"
        aria-label="Kontekst planowania z kolejki"
      >
        <p className="text-xs font-semibold uppercase tracking-wide text-primary-light-400">
          Kontekst planowania
        </p>
        {selectedPlanningRequest && selectedPlanningRequestId && selectedDevice ? (
          <div className="mt-2 min-w-0">
            <p className="line-clamp-2 break-words text-sm font-semibold text-primary-light-100">
              Planujesz: {selectedDevice.label}
            </p>
            {selectedDevice.brandModelLabel ? (
              <p className="mt-1 truncate text-xs text-primary-light-400">
                {selectedDevice.brandModelLabel}
              </p>
            ) : null}
            <p className="mt-2 text-xs text-primary-light-300">{selectedPlanLabel}</p>
            <div className="mt-3 grid gap-2">
              {detailHref ? (
                <Link to={detailHref} className="rs-action-navigation rs-action-md focus-outline">
                  Otwórz sprawę
                </Link>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="mt-2 rounded-lg bg-primary-dark-100/18 px-3 py-3 text-sm leading-snug text-primary-light-400">
            Brak wybranego zlecenia do planowania.
          </p>
        )}
      </section>
    </aside>
  );
};

const PlannerCompanionDayAgendaRow: React.FC<{
  day: ServicePlannerWeekDay;
  selected: boolean;
  onSelectDay: (dayKey: string) => void;
  buildDetailHref: (requestId: string) => string;
}> = ({ day, selected, onSelectDay, buildDetailHref }) => {
  const hasWork = day.items.length > 0;
  const visibleItems = day.items.slice(0, 3);
  const overflowCount = Math.max(0, day.items.length - visibleItems.length);
  const daySummary = formatPlannerCompanionWorkloadSummary(day.workloadSummary);

  return (
    <article
      className={`rs-command-planner-week-day-row ${
        hasWork ? 'rs-command-planner-week-day-row--work' : ''
      } ${selected ? 'rs-command-planner-week-day-row--selected' : ''}`}
      data-testid={`service-planner-week-agenda-day-${day.dayKey}`}
      data-has-work={hasWork ? 'true' : 'false'}
      data-selected-day={selected ? 'true' : 'false'}
      data-load-level={day.loadLevel}
    >
      <button
        type="button"
        aria-pressed={selected}
        className="rs-command-planner-day-selector"
        onClick={() => onSelectDay(day.dayKey)}
      >
        <span className="truncate text-xs font-semibold text-primary-light-100">
          {formatPlannerCompanionWeekday(day.dayKey)}
        </span>
        <span className="truncate text-[11px] text-primary-light-400">
          {formatPlannerCompanionDateShort(day.dayKey)}
        </span>
        <span className="mt-1 rounded-md bg-primary-dark-200/40 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-primary-light-200">
          {day.scheduledCount}
        </span>
      </button>

      <div className="rs-command-planner-day-content">
        <div className="rs-command-planner-day-summary">
          <p className="min-w-0 text-xs font-medium text-primary-light-300">{daySummary}</p>
          {day.clientPreferenceCount > 0 ? (
            <span className="rounded-md bg-primary-dark-200/30 px-1.5 py-0.5 text-[11px] font-medium text-primary-light-500">
              {day.clientPreferenceCount} pref.
            </span>
          ) : null}
          {day.loadLevel === 'heavy' ? (
            <span className="rounded-md bg-status-warning-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-status-warning-300">
              Dużo pracy
            </span>
          ) : null}
        </div>

        {hasWork ? (
          <ul className="rs-command-planner-day-rail">
            {visibleItems.map((item) => (
              <PlannerCompanionAgendaItem
                key={item.requestId}
                item={item}
                detailHref={buildDetailHref(item.requestId)}
              />
            ))}
            {overflowCount > 0 ? (
              <li className="rs-command-planner-agenda-overflow">
                +{overflowCount} więcej
                <span className="sr-only"> więcej w pełnym planerze</span>
              </li>
            ) : null}
          </ul>
        ) : (
          <p className="mt-1.5 text-xs text-primary-light-500">Bez pracy</p>
        )}
      </div>
    </article>
  );
};

const PlannerCompanionAgendaItem: React.FC<{
  item: ServicePlannerItem;
  detailHref: string;
}> = ({ item, detailHref }) => {
  const device = getRepairRequestDevicePresentation(item.request);
  const timeLabel = item.scheduledStartAt
    ? formatPlannerCompanionTime(item.scheduledStartAt)
    : null;
  const clientMeta =
    item.request.clientDetails?.displayName ||
    item.request.clientDetails?.email ||
    'Klient zlecenia';
  const primaryMeta = [timeLabel, device.label, device.brandModelLabel, clientMeta]
    .filter(Boolean)
    .join(' · ');
  const statusLabel = getRepairRequestStatusLabel(item.request.status);

  return (
    <li
      className="rs-command-planner-agenda-item"
      data-testid={`service-planner-companion-item-${item.requestId}`}
      data-conflict={item.hasScheduleConflict ? 'true' : 'false'}
    >
      <div className="min-w-0">
        <p className="line-clamp-2 break-words text-xs font-semibold leading-snug text-primary-light-100">
          {primaryMeta}
        </p>
        <p className="mt-0.5 truncate text-[11px] text-primary-light-500">{statusLabel}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {item.hasScheduleConflict ? (
          <span className="rounded-md bg-status-error-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-status-error-300">
            Kolizja
          </span>
        ) : null}
        <Link to={detailHref} className="rs-action-navigation rs-action-compact focus-outline">
          Otwórz
        </Link>
      </div>
    </li>
  );
};

const ServiceOnboardingState: React.FC<{
  status: ServiceVerificationStatus;
  hasProfile: boolean;
  isProfileComplete: boolean;
  reviewReason: string | null;
  onRefresh: () => void;
  onOpenProfileForm: () => void;
  onOpenClientRequests: () => void;
}> = ({
  status,
  hasProfile,
  isProfileComplete,
  reviewReason,
  onRefresh,
  onOpenProfileForm,
  onOpenClientRequests,
}) => {
  if (status === 'pending_approval') {
    return (
      <div className="py-3 md:py-12">
        <div className="mx-auto w-full max-w-3xl space-y-4 px-3 sm:px-4 lg:px-6">
          <Card className="p-5 md:p-6">
            <h1 className="text-xl font-semibold text-primary-light-100">
              Profil serwisu oczekuje na akceptację
            </h1>
            <p className="mt-2 text-sm text-primary-light-300">
              Dane serwisu zostały wysłane do weryfikacji. Panel operacyjny ze zleceniami odblokuje
              się automatycznie po decyzji administratora.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Button variant="neutral" onClick={onRefresh}>
                Odśwież status
              </Button>
              <Button variant="secondary" onClick={onOpenClientRequests}>
                Przejdź do funkcji klienta
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (status === 'changes_requested') {
    return (
      <div className="py-3 md:py-12">
        <div className="mx-auto w-full max-w-3xl space-y-4 px-3 sm:px-4 lg:px-6">
          <Card className="p-5 md:p-6">
            <h1 className="text-xl font-semibold text-primary-light-100">
              Profil serwisu wymaga poprawek
            </h1>
            <p className="mt-2 text-sm text-primary-light-300">
              Administrator poprosił o poprawienie danych. Zaktualizuj profil i wyślij go ponownie
              do weryfikacji.
            </p>
            {reviewReason && (
              <Alert variant="warning" className="mt-4">
                {reviewReason}
              </Alert>
            )}
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Button variant="primary" onClick={onOpenProfileForm}>
                Popraw dane i wyślij ponownie
              </Button>
              <Button variant="secondary" onClick={onOpenClientRequests}>
                Przejdź do funkcji klienta
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="py-3 md:py-12">
      <div className="mx-auto w-full max-w-3xl space-y-4 px-3 sm:px-4 lg:px-6">
        <Card className="p-5 md:p-6">
          <h1 className="text-xl font-semibold text-primary-light-100">
            Uzupełnij onboarding serwisu
          </h1>
          <p className="mt-2 text-sm text-primary-light-300">
            {hasProfile
              ? isProfileComplete
                ? 'Profil ma komplet danych, ale nie został jeszcze wysłany do weryfikacji.'
                : 'Profil serwisu jest niekompletny. Uzupełnij wymagane dane przed wysłaniem.'
              : 'Nie znaleziono profilu serwisu dla tego konta. Uzupełnij dane, aby rozpocząć weryfikację.'}
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button variant="primary" onClick={onOpenProfileForm}>
              Uzupełnij dane serwisu
            </Button>
            <Button variant="secondary" onClick={onOpenClientRequests}>
              Przejdź do funkcji klienta
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

const OrderStatusBucketFilter: React.FC<{
  tabs: OrdersFilterTabConfig[];
  activeOrderFilter: ActiveOrderFilter;
  onToggle: (nextFilter: ActiveOrderFilter) => void;
}> = ({ tabs, activeOrderFilter, onToggle }) => (
  <div
    role="tablist"
    aria-label="Filtry listy zleceń serwisu"
    data-testid="service-order-filter-tablist"
    className="rs-queue-filter"
  >
    {tabs.map((tab) => {
      const isActive = activeOrderFilter === tab.key;
      return (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={isActive}
          aria-controls="service-dashboard-queue-content"
          data-filter-tab={tab.key}
          data-status-tab-slot={tab.key}
          data-state={isActive ? 'active' : 'inactive'}
          onClick={() => onToggle(tab.key)}
          className={`rs-queue-filter-tab ${getRequestQueueFilterTabClasses(
            tab.tone,
            tab.count,
            isActive
          )}`}
        >
          {isActive ? (
            <span
              aria-hidden="true"
              className={`rs-queue-filter-tab-rail ${getRequestQueueFilterActiveRailClasses(
                tab.railKey
              )}`}
            />
          ) : null}
          <span
            className={`relative z-10 flex min-w-0 items-center justify-center gap-1.5 ${isActive ? 'pl-3' : ''}`}
          >
            <span className="truncate text-[11px] font-medium leading-none text-inherit sm:text-[12px]">
              {tab.label}
            </span>
            <span
              className={`shrink-0 text-[12px] font-semibold leading-none tabular-nums ${getRequestQueueFilterCountClasses(
                tab.tone,
                tab.count,
                isActive
              )}`}
            >
              {tab.count}
            </span>
          </span>
        </button>
      );
    })}
  </div>
);

const serializeServiceOrderFilter = (activeOrderFilter: ActiveOrderFilter): string | null =>
  activeOrderFilter === 'all' ? null : activeOrderFilter;

const getServiceOrderFilterFromSearch = (search: string): ActiveOrderFilter => {
  const params = new URLSearchParams(search);
  if (params.get('history') === '1' || String(params.get('historyRequest') || '').trim()) {
    return 'history';
  }
  const rawFilter = String(params.get('filter') || '')
    .trim()
    .toLowerCase();
  if (rawFilter === 'attention' || rawFilter === 'in_progress' || rawFilter === 'history') {
    return rawFilter;
  }
  if (rawFilter === 'reaction') {
    return 'attention';
  }
  if (rawFilter === 'inprogress') {
    return 'in_progress';
  }
  return 'all';
};

const getServiceExpandedRequestFromSearch = (search: string): string | null => {
  const params = new URLSearchParams(search);
  const requestId = String(
    params.get('request') || params.get('activeRequest') || params.get('historyRequest') || ''
  ).trim();
  return requestId || null;
};

const deriveServiceOrdersWorkbench = (requests: RepairRequest[]): DerivedServiceOrders => {
  const grouped: Record<OrdersWorkbenchBucket, DerivedOrderRecord[]> = {
    attention: [],
    in_progress: [],
    history: [],
  };
  const bucketByRequestId: Record<string, OrdersWorkbenchBucket> = {};

  requests.forEach((request) => {
    const statusBucket = getServiceDashboardBucket(request);
    const runtime = buildOrderCardState({
      request,
      actor: 'service',
      variant: 'expanded',
      counterpartLabel: '',
      showReview: false,
    });
    const requiresAttentionSignal =
      statusBucket !== 'history' &&
      (runtime.requiresAttention || request.serviceHasPendingAction === true);
    const bucket: OrdersWorkbenchBucket =
      statusBucket === 'history'
        ? 'history'
        : requiresAttentionSignal
          ? 'attention'
          : 'in_progress';

    grouped[bucket].push({
      request,
      bucket,
      lastRelevantActivityAtMs: resolveLastRelevantActivityAtMs(request),
      requiresAttentionSignal,
    });
    const requestId = getRepairRequestId(request);
    if (requestId) {
      bucketByRequestId[requestId] = bucket;
    }
  });

  const sortByLastRelevantDesc = (records: DerivedOrderRecord[]): DerivedOrderRecord[] =>
    [...records].sort((a, b) => b.lastRelevantActivityAtMs - a.lastRelevantActivityAtMs);

  const attention = sortByLastRelevantDesc(grouped.attention);
  const inProgress = sortByLastRelevantDesc(grouped.in_progress);
  const history = sortByLastRelevantDesc(grouped.history);
  const active = [...attention, ...inProgress];
  const now = new Date();

  return {
    all: [...attention, ...inProgress, ...history].map((item) => item.request),
    attention: attention.map((item) => item.request),
    inProgress: inProgress.map((item) => item.request),
    history: history.map((item) => item.request),
    staleActive: active
      .filter((item) => isStaleRepairRequest(item.request, 48, now))
      .map((item) => item.request),
    bucketByRequestId,
    counts: {
      attention: attention.length,
      in_progress: inProgress.length,
      history: history.length,
    },
  };
};

const resolveServiceQueueAttentionKind = (
  request: RepairRequest
): ServiceQueueAttentionKind | null => {
  if (getServiceDashboardBucket(request) === 'history') {
    return null;
  }

  const unread = getRepairRequestUnreadState(request, 'service');
  if (unread.hasUnreadMessages) {
    return 'unread_messages';
  }
  if (unread.hasFreshRequestActivity) {
    return 'fresh_request';
  }

  const runtime = buildOrderCardState({
    request,
    actor: 'service',
    variant: 'expanded',
    counterpartLabel: '',
    showReview: false,
  });

  return runtime.requiresAttention || request.serviceHasPendingAction === true
    ? 'needs_action'
    : null;
};

const resolveLastRelevantActivityAtMs = (request: RepairRequest): number =>
  toTimestampMs(request.lastServiceActivityAt) ||
  toTimestampMs(request.updatedAt) ||
  toTimestampMs(request.createdAt);

const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() => getMediaQueryMatches(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setMatches(false);
      return undefined;
    }

    const mediaQuery = window.matchMedia(query);
    const handleChange = () => setMatches(mediaQuery.matches);
    handleChange();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [query]);

  return matches;
};

const getMediaQueryMatches = (query: string): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(query).matches;
};

const plannerCompanionDayFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
});

const plannerCompanionWeekdayFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  weekday: 'short',
});

const plannerCompanionDateShortFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  day: '2-digit',
  month: '2-digit',
});

const plannerCompanionTimeFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  hour: '2-digit',
  minute: '2-digit',
});

const formatPlannerCompanionDayLabel = (dayKey: string): string => {
  const date = dateFromServicePlannerDayKey(dayKey);
  return Number.isFinite(date.getTime()) ? plannerCompanionDayFormatter.format(date) : dayKey;
};

const formatPlannerCompanionWeekday = (dayKey: string): string => {
  const date = dateFromServicePlannerDayKey(dayKey);
  return Number.isFinite(date.getTime())
    ? plannerCompanionWeekdayFormatter.format(date).replace('.', '')
    : dayKey;
};

const formatPlannerCompanionDateShort = (dayKey: string): string => {
  const date = dateFromServicePlannerDayKey(dayKey);
  return Number.isFinite(date.getTime()) ? plannerCompanionDateShortFormatter.format(date) : dayKey;
};

const formatPlannerCompanionWeekRange = (
  weekItems: Array<{ dayKey: string; date: Date }>
): string => {
  const first = weekItems[0]?.date;
  const last = weekItems[weekItems.length - 1]?.date;
  if (!first || !last) {
    return 'Tydzień';
  }
  const firstLabel = plannerCompanionDayFormatter.format(first);
  const lastLabel = plannerCompanionDayFormatter.format(last);
  return `${firstLabel} - ${lastLabel}`;
};

const formatPlannerCompanionCompactWeekRange = (
  weekItems: Array<{ dayKey: string; date: Date }>
): string => {
  const first = weekItems[0]?.date;
  const last = weekItems[weekItems.length - 1]?.date;
  if (!first || !last) {
    return 'Tydzień';
  }
  const firstLabel = plannerCompanionDateShortFormatter.format(first);
  const lastLabel = plannerCompanionDateShortFormatter.format(last);
  return `${firstLabel}-${lastLabel}`;
};

const formatPlannerCompanionWeekNumberLabel = (
  weekItems: Array<{ dayKey: string; date: Date }>
): string => {
  const first = weekItems[0]?.date;
  if (!first || !Number.isFinite(first.getTime())) {
    return 'tydz. --';
  }
  return `tydz. ${getIsoWeekNumber(first)}`;
};

const getIsoWeekNumber = (date: Date): number => {
  const normalized = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
  const day = normalized.getUTCDay() || 7;
  normalized.setUTCDate(normalized.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(normalized.getUTCFullYear(), 0, 1));
  return Math.ceil(((normalized.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

const formatPlannerCompanionTime = (date: Date): string =>
  plannerCompanionTimeFormatter.format(date);

const formatPlannerCompanionWorkloadCount = (count: number): string => {
  const normalizedCount = Math.max(0, count);
  if (normalizedCount === 1) {
    return '1 zlecenie';
  }
  const mod10 = normalizedCount % 10;
  const mod100 = normalizedCount % 100;
  const noun = mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14) ? 'zlecenia' : 'zleceń';
  return `${normalizedCount} ${noun}`;
};

const formatPlannerCompanionWorkloadSummary = (
  summary: ServicePlannerDayWorkloadSummary
): string => {
  const countLabel = formatPlannerCompanionWorkloadCount(summary.scheduledCount);
  const firstTime = summary.firstScheduledAt
    ? formatPlannerCompanionTime(summary.firstScheduledAt)
    : null;
  const lastTime = summary.lastScheduledAt
    ? formatPlannerCompanionTime(summary.lastScheduledAt)
    : null;
  if (!firstTime) {
    return countLabel;
  }
  if (!lastTime || firstTime === lastTime) {
    return `${countLabel} · ${firstTime}`;
  }
  return `${countLabel} · ${firstTime}-${lastTime}`;
};

const formatServiceTimestamp = (value: unknown): string | null => {
  const date = toDateValue(value);
  if (!date) {
    return null;
  }
  return new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const toDateValue = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const date = (value as { toDate: () => Date }).toDate();
    return Number.isFinite(date.getTime()) ? date : null;
  }
  return null;
};

const toTimestampMs = (value: unknown): number => {
  if (!value) return 0;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const maybeDate = (value as { toDate?: () => Date }).toDate?.();
    if (maybeDate instanceof Date) {
      return maybeDate.getTime();
    }
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return 0;
};

export default ServiceDashboardPage;
